import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { analyticsAPI } from '../../services/api';
import TraineeSummaryModal from '../../components/common/TraineeSummaryModal';
import {
  ArrowLeft, BookOpen, Clock, TrendingUp, Users,
  Activity, ChevronDown, ChevronUp, CheckCircle2, CalendarClock
} from 'lucide-react';
import './Analytics.css';

const ModuleAnalytics = () => {
  const { id: moduleId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [previewTraineeRef, setPreviewTraineeRef] = useState(null);

  useEffect(() => {
    const fetchModuleAnalytics = async () => {
      try {
        setLoading(true);
        const response = await analyticsAPI.getModuleAnalytics(moduleId);
        if (response.data.success) {
          const result = response.data.data;
          setAnalytics(result);
          // Canonicalize URL to dashless GUID if the user landed on a numeric id
          const guid = result?.module?.moduleId;
          if (guid && /^\d+$/.test(String(moduleId))) {
            const dashless = guid.replace(/-/g, '');
            navigate(`/analytics/module/${dashless}`, { replace: true });
          }
        } else {
          setError('Failed to fetch module analytics');
        }
      } catch (err) {
        console.error('Error fetching module analytics:', err);
        const msg = err.response?.data?.error || 'Failed to load module analytics';
        setError(msg);
        showNotification(msg, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchModuleAnalytics();
  }, [moduleId, showNotification]);

  if (loading) {
    return (
      <div className="analytics">
        <div className="page-header">
          <h1>Loading module analytics...</h1>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="analytics">
        <div className="page-header">
          <div className="page-header-left">
            <button className="page-back" onClick={() => navigate('/analytics')}>
              <ArrowLeft size={12} /> Back to Analytics
            </button>
            <h1>Error loading module analytics</h1>
            <p>{error || 'Analytics data not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { module, traineeProgress = [], recentEvents = [] } = analytics;
  const visibleEvents = showAllEvents ? recentEvents : recentEvents.slice(0, 15);

  const toggleEventExpanded = (eventId) => {
    setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const formatEventName = (name) =>
    name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div className="analytics">
      <div className="page-header">
        <div className="page-header-left">
          <button className="page-back" onClick={() => navigate('/analytics')}>
            <ArrowLeft size={12} /> Back to Analytics
          </button>
          <h1>
            <BookOpen size={20} style={{ marginRight: '8px' }} />
            {module.title}
          </h1>
          <p>
            {module.category && <span className="badge badge-dark" style={{ marginRight: '8px' }}>{module.category}</span>}
            {module.level && <span className="badge badge-gray" style={{ marginRight: '8px' }}>{module.level}</span>}
            <span className={`badge ${module.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>{module.status}</span>
            {module.duration ? <span style={{ marginLeft: '8px' }}>• {module.duration} min</span> : null}
            {module.contentCount ? <span style={{ marginLeft: '8px' }}>• {module.contentCount} content items</span> : null}
          </p>
          {module.description && (
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem' }}>{module.description}</p>
          )}
        </div>
      </div>

      {/* Stats Row — 4 high-signal KPIs */}
      <div className="analytics__stats analytics__stats--four">
        {[
          {
            icon: Users,
            label: 'Trainees Enrolled',
            value: module.enrolledCount,
            sub: `${module.completedCount} completed · ${module.inProgressCount} in progress`,
            color: 'blue',
          },
          {
            icon: TrendingUp,
            label: 'Completion Rate',
            value: `${module.completionRate}%`,
            sub: `Avg progress ${module.averageProgress}%`,
            color: 'green',
          },
          {
            icon: Clock,
            label: 'Total Time Spent',
            value: `${module.totalTimeSpentHours}h`,
            sub: module.enrolledCount
              ? `${Math.round((module.totalTimeSpentSeconds / Math.max(module.enrolledCount, 1) / 60))} min/trainee avg`
              : null,
            color: 'dark',
          },
          {
            icon: CalendarClock,
            label: 'Last Activity',
            value: module.lastActivity ? new Date(module.lastActivity).toLocaleDateString() : '—',
            sub: module.lastActivity ? new Date(module.lastActivity).toLocaleTimeString() : null,
            color: 'yellow',
          },
        ].map(({ icon: Icon, label, value, sub, color }) => (
          <div key={label} className={`analytics__stat-card analytics__stat-card--${color}`}>
            <div className="analytics__stat-icon">
              <Icon size={20} />
            </div>
            <div className="analytics__stat-body">
              <span className="analytics__stat-value">{value}</span>
              <span className="analytics__stat-label">{label}</span>
              {sub && <span className="analytics__stat-sub">{sub}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Trainee Progress Table */}
      <div className="card">
        <div className="card-header">
          <h3><Users size={16} style={{ marginRight: '6px' }} /> Trainee Progress</h3>
          <span className="badge badge-dark">{traineeProgress.length} trainees</span>
        </div>
        {traineeProgress.length > 0 ? (
          <div className="analytics__table">
            <div className="analytics__table-header">
              <span>Trainee</span>
              <span>Department</span>
              <span>Progress</span>
              <span>Time Spent</span>
              <span>Last Accessed</span>
              <span>Status</span>
            </div>
            {traineeProgress.map((t) => (
              <div
                key={t.traineeId}
                className="analytics__table-row"
                style={{ cursor: 'pointer' }}
                onClick={() => setPreviewTraineeRef(t.traineeUuid ? t.traineeUuid.replace(/-/g, '') : t.traineeId)}
                title="Preview trainee analytics"
              >
                <div className="analytics__table-module">
                  <div className="analytics__table-icon"><Users size={14} /></div>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ display: 'block' }}>{t.name}</span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{t.email}</span>
                  </div>
                </div>
                <span className="analytics__table-cell">{t.department || '—'}</span>
                <div className="analytics__table-progress">
                  <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                    <div className="progress-bar-fill" style={{ width: `${t.progress}%` }} />
                  </div>
                  <span
                    className={`analytics__table-pct ${
                      t.progress >= 75 ? 'analytics__table-pct--high' :
                      t.progress >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                    }`}
                  >
                    {t.progress}%
                  </span>
                </div>
                <span className="analytics__table-cell">{t.timeSpentHours}h</span>
                <span className="analytics__table-cell" style={{ fontSize: '0.75rem' }}>
                  {t.lastAccessed ? new Date(t.lastAccessed).toLocaleDateString() : '—'}
                </span>
                <span className={`badge ${
                  t.completed ? 'badge-green' : t.progress > 0 ? 'badge-yellow' : 'badge-gray'
                }`}>
                  {t.completed ? (
                    <><CheckCircle2 size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Completed</>
                  ) : t.progress > 0 ? 'In Progress' : 'Not Started'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            <Users size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
            <p>No trainees enrolled in this module yet.</p>
          </div>
        )}
      </div>

      {/* Recent Events */}
      <div className="card">
        <div className="card-header">
          <h3><Activity size={16} style={{ marginRight: '6px' }} /> Recent Activity</h3>
          <span className="badge badge-dark">{recentEvents.length} events</span>
        </div>
        {recentEvents.length > 0 ? (
          <>
            <div className="analytics__table">
              <div className="analytics__table-header analytics__table-header--events">
                <span>Event</span>
                <span>Time</span>
                <span>Trainee</span>
                <span>Content</span>
                <span>Scene</span>
                <span>Details</span>
              </div>
              {visibleEvents.map((event) => (
                <React.Fragment key={event.id}>
                  <div className="analytics__table-row analytics__table-row--events">
                    <span className="analytics__table-cell">
                      <span className="analytics__event-badge">{formatEventName(event.eventName)}</span>
                    </span>
                    <span className="analytics__table-cell" style={{ fontSize: '0.75rem' }}>
                      {new Date(event.eventTime).toLocaleString()}
                    </span>
                    <span className="analytics__table-cell">
                      {event.userName || (event.userId ? `User ${event.userId}` : '—')}
                    </span>
                    <span className="analytics__table-cell">
                      {event.contentId ? `Content ${event.contentId}` : '—'}
                    </span>
                    <span className="analytics__table-cell">{event.sceneName || '—'}</span>
                    <span className="analytics__table-cell">
                      {event.data && Object.keys(event.data).length > 0 ? (
                        <button
                          className="btn btn-outline"
                          style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                          onClick={() => toggleEventExpanded(event.id)}
                        >
                          {expandedEvents[event.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          {expandedEvents[event.id] ? ' Hide' : ' View'}
                        </button>
                      ) : '—'}
                    </span>
                  </div>
                  {expandedEvents[event.id] && event.data && (
                    <div className="analytics__event-data">
                      <pre>{JSON.stringify(event.data, null, 2)}</pre>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            {recentEvents.length > 15 && (
              <div style={{ padding: 'var(--space-3) var(--space-5)', textAlign: 'center' }}>
                <button className="btn btn-outline" onClick={() => setShowAllEvents(!showAllEvents)}>
                  {showAllEvents ? 'Show Less' : `Show All ${recentEvents.length} Events`}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="card-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            <Activity size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
            <p>No analytics events recorded for this module yet.</p>
          </div>
        )}
      </div>

      <TraineeSummaryModal
        isOpen={!!previewTraineeRef}
        onClose={() => setPreviewTraineeRef(null)}
        traineeRef={previewTraineeRef}
      />
    </div>
  );
};

export default ModuleAnalytics;
